import { useOrganizationGroups } from "@/api/organization-groups";
import {
    AdminPageContent,
    AdminPageHeader,
    SearchField,
} from "@/components/admin";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useMountEffect } from "@react-hookz/web";
import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { CreateGroupDialog } from "./components/CreateGroupDialog";
import { GroupsTable } from "./components/GroupsTable";

const DEFAULT_PAGE_SIZE = 25;

const OrganizationGroups = () => {
    const { setBreadcrumbs } = useBreadcrumb();

    useDocumentTitle("Groups");

    const { search, debouncedSearch, setSearch, clear } = useDebouncedSearch();
    const [requestedPage, setRequestedPage] = useState(1);
    const [requestedSize, setRequestedSize] = useState(DEFAULT_PAGE_SIZE);

    useMountEffect(() => {
        setBreadcrumbs([{ label: "Admin", path: "home" }, { label: "Groups" }]);
    });

    useEffect(() => {
        setRequestedPage(1);
    }, [debouncedSearch]);

    const { data, isLoading, isError, refetch } = useOrganizationGroups({
        page: requestedPage,
        size: requestedSize,
        search: debouncedSearch,
    });

    const isRecoveringPage = Boolean(
        data && data.page > Math.max(1, data.pages)
    );

    useEffect(() => {
        if (isRecoveringPage && data) {
            setRequestedPage(Math.max(1, data.pages));
        }
    }, [data, isRecoveringPage]);

    const pagination: PaginationState = {
        pageIndex: (data?.page ?? requestedPage) - 1,
        pageSize: data?.size ?? requestedSize,
    };

    const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
        const next =
            typeof updater === "function" ? updater(pagination) : updater;
        setRequestedPage(next.pageIndex + 1);
        setRequestedSize(next.pageSize);
    };

    return (
        <AdminPageContent>
            <AdminPageHeader
                title="Groups"
                subtitle="Share a knowledge base and skills between organizations"
            >
                <CreateGroupDialog />
            </AdminPageHeader>

            <div className="mt-6">
                <div className="pb-4">
                    <SearchField
                        value={search}
                        onChange={setSearch}
                        onClear={clear}
                        placeholder="Search groups..."
                    />
                </div>
                <GroupsTable
                    data={data?.items ?? []}
                    rowCount={data?.total}
                    isLoading={isLoading}
                    isError={isError}
                    isRecoveringPage={isRecoveringPage}
                    hasSearch={!!debouncedSearch}
                    onClearSearch={clear}
                    onRetry={() => refetch()}
                    pagination={pagination}
                    onPaginationChange={handlePaginationChange}
                />
            </div>
        </AdminPageContent>
    );
};

export default OrganizationGroups;
